/**
 * The navy band at the top of the two candidate conflict check pages.
 *
 * WHY THESE PAGES HAVE THEIR OWN. /ccc and the candidate's form both sit outside the app Shell —
 * they are a separate tool that shares a deployment — so neither gets the rail, the header or the
 * firm mark that every other page in this app is framed by. Until now each opened on a bare
 * heading over a grey page, which for the candidate is the first thing they see of the firm after
 * an offer and looked like an untitled form somebody had linked them to.
 *
 * It is deliberately the same shape and colour as the header band on the invitation email
 * (mail/candidateMail.js): the candidate arrives here by clicking that email, and the page they
 * land on looking like the message that sent them is most of what tells them the link is real.
 *
 * THE MARK is the candidate form's, not the intake app's — the same one in the browser tab. These
 * pages are not the New Matter Intake app and should not claim to be.
 *
 * THE FIRM'S LOGO (`logo`) replaces the mark on the application for employment, which an
 * applicant opens as the firm's own page. It is the simple "aalrr" in white with the green l, the
 * version the firm's graphics manager recommends; the full name is already on the line beside it.
 * Off unless a page asks, so the conflict-check pages keep the mark they have.
 *
 * THE VERSION PILL is for the firm's side only. A candidate has no use for a build number, no way
 * to act on it, and no business reading a changelog of the firm's internal tools; it would be
 * noise at best and a small information leak at worst.
 */
import VersionPill from './VersionPill';
import { useAuth } from '../auth/AuthProvider';

/**
 * @param {boolean} [showSignOut]  an EXPLICIT flag, not inferred from `showVersion`.
 *
 * This banner sits on internal pages and on the two public forms an external applicant opens from
 * a link. Putting a sign-out in front of somebody who has no account would be confusing at best,
 * and would imply they have one. So it is off unless a page asks for it, rather than deduced from
 * whichever other flag happens to correlate today.
 */
export default function CandidateBanner({
  title,
  showVersion = false,
  showSignOut = false,
  width = 'max-w-4xl',
  // Phones only: the build number is the first thing to give up its room when the title and the
  // firm's name would otherwise wrap to three and four lines. Off unless a page asks.
  hideVersionOnPhone = false,
  logo = false,
}) {
  const { signOut, displayName } = useAuth();
  return (
    <header className="bg-firm-700 dark:bg-firm-800">
      <div className={`mx-auto flex ${width} items-center gap-3 px-4 py-3 sm:px-6`}>
        {logo ? (
          <img src="/aalrr-logo.png" alt="AALRR" width="90" height="36" className="h-9 w-auto shrink-0" />
        ) : (
          <img src="/ccc-favicon.svg" alt="" aria-hidden="true" className="h-9 w-9 shrink-0" />
        )}
        <div className="min-w-0">
          {/* Wraps rather than truncates: the candidate's title is eight words, and on a phone
              `truncate` cut it to "Candidate conflict check and professional discl…" — a title
              that trails off is worse than one on two lines. */}
          <h1 className="text-[17px] font-semibold leading-tight text-white">{title}</h1>
          {/* The firm's name in full, once, at the top. Every page under this banner used to
              repeat it as a subtitle of its own heading. */}
          <p className="text-xs text-firm-200">Atkinson, Andelson, Loya, Ruud &amp; Romo</p>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-3">
          {showVersion ? (
            hideVersionOnPhone ? (
              <span className="hidden sm:inline-flex">
                <VersionPill />
              </span>
            ) : (
              <VersionPill />
            )
          ) : null}
          {showSignOut ? (
            <>
              {displayName ? (
                <span className="hidden text-xs text-firm-200 sm:inline">{displayName}</span>
              ) : null}
              <button
                type="button"
                onClick={signOut}
                className="rounded border border-white/25 px-2.5 py-1 text-xs text-white hover:bg-white/10"
              >
                Sign out
              </button>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}
